import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import maplibregl, { Map } from 'maplibre-gl';
import Router from 'next/router';
import { useMap } from '@/hooks/useMap';
import { formatNumber, getTotalKm, sanitizeRegion } from '@/utils/explorer';
import { IRegion } from '@/types/explorer';
import * as CoverageData from '@/components/ui/map/sources/CoverageData';
import * as NewCoverageData from '@/components/ui/map/sources/NewCoverageData';
import * as RegionLabels from '@/components/ui/map/sources/RegionLabels';
import * as Regions from '@/components/ui/map/layers/Regions';
import { addLayer as addCoverageLayer } from '@/components/ui/map/layers/Coverage';

interface Props {
  children?: React.ReactNode;
  region?: IRegion;
  regions?: IRegion[];
}

function RegionMap(props: Props) {
  const { region, regions } = props;
  const mapMounted = useRef(false);
  const regionHoveredRef = useRef<any>(null);
  const mapWrapperRef = useRef<any>(null);
  const { map, setAreLayersReady, mapContainerRef } = useMap();
  const [hoveredRegionId, setHoveredRegionId] = useState<string | null>(null);

  const onRegionClicked = useCallback(
    (e: any) => {
      const regionId = e.features?.[0]?.properties?.id;
      const regionData = regions?.find(region => region.id === regionId);
      const regionName = sanitizeRegion(regionData?.name || '');
      if (regionName) {
        Router.push(`/network/regions/${regionName}/metrics`);
      }
    },
    [regions],
  );

  const MarkerContent = ({ region }: { region: IRegion }) => {
    return (
      <div className="flex flex-col gap-2 rounded-lg bg-neutral-900 p-4">
        <div className="text-base font-bold">{region.name}</div>
        <div className="flex gap-8 text-[10px]">
          <div className="flex flex-col leading-normal">
            <span className="font-bold uppercase">Total Road Km Mapped</span>
            <span>
              {formatNumber(getTotalKm(region.totalCoveredL12Cells ?? 0))}
            </span>
          </div>
          <div className="flex flex-col leading-normal">
            <span className="font-bold uppercase">Unique Road Km Mapped</span>
            <span>
              {formatNumber(getTotalKm(region.uniqueCoveredL12Cells ?? 0))}
            </span>
          </div>
          <div className="flex flex-col leading-normal">
            <span className="font-bold uppercase">Contributors</span>
            <span>{formatNumber(region.totalUsers ?? 0)}</span>
          </div>
        </div>
      </div>
    );
  };

  const locateRegion = useCallback(
    (map: maplibregl.Map, region: IRegion | undefined) => {
      if (!map || !region) {
        return;
      }

      const { lat, lon } = region.focalPoint || {};
      if (!lat || !lon) {
        return;
      }

      const markerDiv = document.createElement('div');
      markerDiv.className = 'marker';

      const root = createRoot(markerDiv);
      root.render(<MarkerContent region={region} />);

      new maplibregl.Marker({ element: markerDiv })
        .setLngLat([lon, lat])
        .addTo(map);

      return () => {
        root.unmount();
        map.remove();
      };
    },
    [],
  );

  const onRegionHovered = useCallback(
    (e: any) => {
      if (!map) return;
      const hoveredRegionId = e?.features?.[0]?.properties?.id;
      setHoveredRegionId(hoveredRegionId);
      if (!regionHoveredRef.current) return;
      regionHoveredRef.current.style.left = `${e?.originalEvent?.pageX || 0}px`;
      regionHoveredRef.current.style.top = `${e?.originalEvent?.pageY || 0}px`;
    },
    [map],
  );

  const initializeData = useCallback(
    async (
      map: Map,
      regionId: string,
      regions: IRegion[],
      setAreLayersReady: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      setAreLayersReady(false);

      // Register Sources
      await CoverageData.addSource(map);
      await NewCoverageData.addSource(map);
      await RegionLabels.addSource(map, regions);

      addCoverageLayer(map);
      Regions.addLayer({
        map,
        selectedRegionId: regionId,
        onRegionClick: onRegionClicked,
        onRegionHovered,
      });

      setAreLayersReady(true);
    },
    [onRegionClicked, onRegionHovered],
  );

  useEffect(() => {
    if (!mapWrapperRef?.current) return;
    const handleMouseMove = (e: any) => {
      if (!regionHoveredRef?.current) {
        return;
      }

      const containerDiv = e.currentTarget;
      const rect = containerDiv.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      regionHoveredRef.current.style.left = `${x + 10}px`;
      regionHoveredRef.current.style.top = `${y + 15}px`;
      regionHoveredRef.current.style.display = 'block';
    };

    mapWrapperRef.current.addEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    if (map && !mapMounted.current && region && regions) {
      initializeData(map, region.id, regions, setAreLayersReady);
      locateRegion(map, region);
      mapMounted.current = true;
    }
  }, [map, initializeData, setAreLayersReady, regions, region, locateRegion]);

  const bbox: any = region?.bbox;

  useEffect(() => {
    if (map && bbox) {
      const [minLng, minLat, maxLng, maxLat] = bbox;

      const bounds: any = [
        [minLng, minLat],
        [maxLng, maxLat],
      ];

      map.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 100, right: 100 },
        animate: true,
      });
    }
  }, [map, bbox]);

  let hoveredRegion = regions?.find(region => region.id === hoveredRegionId);
  if (hoveredRegion?.id === region?.id) {
    hoveredRegion = undefined;
  }

  return (
    <>
      <div
        ref={mapWrapperRef}
        className="relative h-[calc(100dvh-192px)] w-full overflow-hidden rounded-lg md:h-[calc(100dvh-204px)] [&_canvas]:h-full">
        <div ref={mapContainerRef} className="size-full" />
        {hoveredRegion ? (
          <div
            ref={regionHoveredRef}
            className="absolute hidden animate-fadeIn">
            <MarkerContent region={hoveredRegion} />
          </div>
        ) : null}
      </div>
    </>
  );
}

export default RegionMap;
